import { Controller } from "@hotwired/stimulus"
import { scoreBits, bucketFor } from "../password_strength"

// EVA-253 Password set-up: live requirement checklist, entropy strength meter,
// instant match feedback, Caps Lock warning, reveal toggle and a submit gate.
//
// Calm-first: an unmet requirement never shows a warning state while the user
// is still in the field for the first time (`touched`). The only red before
// submit is a confirmation mismatch, which is a fact rather than a judgement.
//
// The gate is bound to the FORM's `submit` event, not to a click on the button:
// Enter in either field must not walk past it. The button itself is rendered
// ENABLED by the server and only ever marked `aria-disabled`, so (a) a keyboard
// or screen-reader user can focus it and be told why they are stuck, and (b) if
// the Vite bundle fails to load, the form still submits and the server rules
// still apply.
//
// Client checks DUPLICATE the server rules (PasswordComplexity + Devise
// password_length, both read from the server via data values). They never
// replace them, and they cannot cover PasswordArchivable (no reuse of the last
// 6 passwords — a bcrypt comparison), so nothing here promises success.
//
// LENGTH IS COUNTED IN CODE POINTS, never in `String#length`. JS `.length` is
// UTF-16 code units, Ruby's `String#size` (Devise's validates_length_of and the
// operation contract's min_size?/max_size?) is characters, and an astral
// character — any emoji — counts 2 against 1. Counting units here would tick
// "At least 12 characters" green on a password the server then rejects for
// being too short, which breaks the widget's entire contract.
export default class extends Controller {
  static targets = [
    "password", "confirmation",
    "toggle", "toggleIcon", "toggleLabel",
    "meter", "meterFill", "meterLabel",
    "rule", "advisory", "capsLock",
    "match", "submit", "status", "announce", "alert"
  ]

  static values = { policy: Object, copy: Object }

  connect() {
    this.touched = false
    this.confirmTouched = false
    this.submitAttempted = false
    this.capsLock = false
    this.lastAnnouncement = ""
    this.states = []

    // A rule pattern that JS cannot compile (Ruby-only syntax reaching the
    // browser) must not take the whole component down. Rather than shipping a
    // checklist with one permanently-dead row — which would lock the gate for
    // every invited user — the component degrades to exactly what a JS-less
    // page shows: the server-rendered static list, no meter, no gate, all
    // enforcement on the server. Loud in the console, silent to the user.
    try {
      this.rules = this.ruleTargets.map((el) => ({
        el,
        code: el.dataset.ruleCode,
        kind: el.dataset.ruleKind,
        pattern: el.dataset.rulePattern ? new RegExp(el.dataset.rulePattern) : null
      }))
    } catch (error) {
      this.degraded = true
      // Whatever state the DOM was in, present the JS-less page: a meter that
      // never moves and a toggle that does nothing are worse than neither.
      this.meterTarget.hidden = true
      this.toggleTarget.hidden = true
      console.error("[password-policy] rule pattern is not valid in JS; " +
                    "falling back to server-side validation only", error)
      return
    }

    this.forbidden = this.policyValue.forbiddenCharacters || []

    // Progressive enhancement: these are hidden in the ERB so a JS-less page
    // shows no dead meter and no non-functioning toggle.
    this.meterTarget.hidden = false
    this.toggleTarget.hidden = false

    this.evaluate()

    // Password managers frequently fill both fields without firing `input`.
    this.autofillTimer = setTimeout(() => this.evaluate(), 500)
  }

  disconnect() {
    clearTimeout(this.autofillTimer)
    clearTimeout(this.announceTimer)
  }

  // ---- helpers -------------------------------------------------------------

  // Code points, not UTF-16 code units. See the class comment.
  length(value) {
    return Array.from(value).length
  }

  // ---- actions -------------------------------------------------------------

  touch() {
    if (this.degraded) return
    if (this.passwordTarget.value.length === 0) return
    this.touched = true
    this.evaluate()
  }

  touchConfirm() {
    if (this.degraded) return
    this.confirmTouched = true
    this.evaluate()
  }

  caps(event) {
    if (this.degraded) return
    const on = typeof event.getModifierState === "function"
      ? event.getModifierState("CapsLock")
      : false
    if (on === this.capsLock) return
    this.capsLock = on
    this.evaluate()
  }

  // Pressing the toggle must not count as leaving the password field: a mouse
  // press would otherwise blur it, fire touch(), and promote every still-unmet
  // rule to its warning state while the user is mid-word. Suppressing the
  // default mousedown keeps focus (and the caret) exactly where it was; a
  // keyboard user, who focused the button deliberately, fires no mousedown and
  // so keeps focus on the button.
  keepFocus(event) {
    event.preventDefault()
  }

  toggleVisibility() {
    if (this.degraded) return

    const copy = this.copyValue.toggle
    const revealing = this.passwordTarget.type === "password"
    const active = document.activeElement
    const selection = active && active.type !== undefined
      ? { start: active.selectionStart, end: active.selectionEnd }
      : null

    for (const field of [this.passwordTarget, this.confirmationTarget]) {
      field.type = revealing ? "text" : "password"
    }

    // Changing `type` can drop focus and always resets the caret.
    if (selection && (active === this.passwordTarget || active === this.confirmationTarget)) {
      active.focus()
      if (selection.start !== null && selection.end !== null) {
        try { active.setSelectionRange(selection.start, selection.end) } catch (_e) { /* unsupported */ }
      }
    }

    // No aria-pressed. A toggle button that BOTH flips aria-pressed and rewrites
    // its accessible name announces "Hide password … pressed" while the state is
    // in fact "revealed" — the documented contradictory pattern. The name alone
    // carries the state, and it matches the visible text so voice control works.
    this.toggleTarget.setAttribute("aria-label", revealing ? copy.hide_aria : copy.show_aria)
    this.toggleLabelTarget.textContent = revealing ? copy.hide : copy.show
    this.toggleIconTarget.className = `bi ${revealing ? "bi-eye-slash" : "bi-eye"}`
  }

  // Bound to the FORM's submit event, so Enter, click and requestSubmit() all
  // pass through here. This is the gate.
  guardSubmit(event) {
    if (this.degraded) return

    // Recompute rather than trusting the attribute paintGate last wrote: a
    // password manager that fills both fields without firing `input` (and after
    // the 500 ms connect() sweep) would otherwise leave a stale "true" here and
    // silently swallow the user's first submit.
    const ready = this.evaluate()

    if (ready) {
      // Never post the form with the fields still revealed. Browser password
      // managers key their "save this password?" prompt largely off the presence
      // of input[type=password] at submit time, and this is the one screen where
      // failing to save the password is the lockout the screen exists to
      // prevent. Firefox's form history also stores type=text values on submit.
      // Only on the submission that actually leaves: re-masking after a blocked
      // Enter would just undo something the user deliberately asked for.
      this.conceal()
      return
    }

    event.preventDefault()
    this.touched = true
    this.confirmTouched = true
    this.submitAttempted = true
    this.evaluate()

    // A rejected submit is exactly the moment that warrants an assertive
    // announcement: the user pressed Enter in the password field, focus does not
    // move, and the polite region composes the same sentence as before and is
    // therefore silent. Announce the reason in the alert region instead.
    this.announceBlocked()

    const passwordProblem = this.passwordProblem()
    ;(passwordProblem ? this.passwordTarget : this.confirmationTarget).focus()
  }

  conceal() {
    for (const field of [this.passwordTarget, this.confirmationTarget]) {
      field.type = "password"
    }
    const copy = this.copyValue.toggle
    this.toggleTarget.setAttribute("aria-label", copy.show_aria)
    this.toggleLabelTarget.textContent = copy.show
    this.toggleIconTarget.className = "bi bi-eye"
  }

  // True when the blocking problem is the password itself rather than the
  // confirmation — including the too-long and line-break cases, which are not
  // checklist rows and so are invisible to states.find().
  passwordProblem() {
    const password = this.passwordTarget.value
    return this.length(password) > this.policyValue.maxLength ||
      this.hasForbidden(password) ||
      this.states.some((state) => !state.met)
  }

  hasForbidden(password) {
    return this.forbidden.some((char) => password.includes(char))
  }

  // Returns whether the form is ready to submit.
  evaluate() {
    if (this.degraded) return true

    const policy = this.policyValue
    const password = this.passwordTarget.value
    const confirmation = this.confirmationTarget.value
    const size = this.length(password)

    this.states = this.rules.map((rule) => ({
      rule,
      met: rule.kind === "length"
        ? size >= policy.minLength
        : rule.pattern.test(password)
    }))

    const met = this.states.filter((state) => state.met).length
    const tooLong = size > policy.maxLength
    const forbidden = this.hasForbidden(password)
    const allMet = met === this.states.length && !tooLong && !forbidden

    const bits = scoreBits(password, policy.tokens)
    const bucket = bucketFor(bits, policy.buckets)

    this.paintRules(password, size)
    this.paintMeter(password, bits, bucket)
    const advisory = this.paintAdvisory(allMet, bucket)
    this.paintCapsLock()

    const match = this.paintMatch(password, confirmation)
    const ready = allMet && confirmation.length > 0 && match === "matched"

    this.paintGate(ready)
    this.lastReason = this.paintStatus({ password, size, confirmation, tooLong, forbidden, match, ready })
    this.scheduleAnnounce({ password, bucket, met, total: this.states.length, match, ready, advisory })

    return ready
  }

  // ---- painting ------------------------------------------------------------

  paintRules(password, size) {
    const copy = this.copyValue
    const minLength = this.policyValue.minLength

    this.states.forEach(({ rule, met }) => {
      const state = met ? "met" : (this.touched ? "outstanding" : "pending")
      const wide = rule.kind === "length" ? " password-policy__rule--wide" : ""

      rule.el.className = `password-policy__rule password-policy__rule--${state}${wide}`
      rule.el.querySelector("[data-role='icon']").className = met
        ? "bi bi-check-circle-fill"
        : (state === "outstanding" ? "bi bi-dash-circle" : "bi bi-circle")
      // Three visual states deserve three strings: "outstanding" is a
      // deliberate escalation (the user has now left the field) and must not be
      // conveyed by colour and icon alone.
      rule.el.querySelector("[data-role='state']").textContent = copy.state[state]

      if (rule.kind === "length") {
        const counter = rule.el.querySelector("[data-role='counter']")
        counter.hidden = met || size === 0
        counter.textContent = copy.counter
          .replace("%{typed}", String(size))
          .replace("%{required}", String(minLength))
      }
    })
  }

  paintMeter(password, bits, bucket) {
    const percent = password.length === 0
      ? 0
      : Math.min(100, Math.round((bits / this.policyValue.fullBarBits) * 100))

    this.meterTarget.className = `password-policy__meter password-policy__meter--${bucket.toLowerCase()}`
    this.meterFillTarget.style.width = `${percent}%`
    this.meterLabelTarget.textContent = password.length === 0
      ? ""
      : this.copyValue.strength.levels[bucket]
  }

  // Returns the advisory text when it is showing, "" otherwise.
  //
  // NOT toggled with `hidden`: this element is referenced by the password
  // field's aria-describedby, and the accname spec includes a DIRECTLY
  // referenced node's text even when it is hidden. Emptying the text is the
  // only way to make it genuinely absent from the description.
  paintAdvisory(allMet, bucket) {
    const show = allMet && this.policyValue.advisoryBuckets.includes(bucket)
    const text = show ? this.copyValue.strength.advisory : ""
    this.advisoryTarget.textContent = text
    return text
  }

  // Caps Lock is advisory, not a blocker, so it gets its own line and never
  // displaces the one sentence that explains why the gate is shut. Same
  // hidden-vs-empty reasoning as the advisory.
  paintCapsLock() {
    this.setLine(this.capsLockTarget, "password-policy__caps", "warn", {
      icon: this.capsLock ? "bi-capslock-fill" : "",
      text: this.capsLock ? this.copyValue.caps_lock : ""
    })
  }

  // Returns "idle" | "matched" | "mismatched".
  // Mid-typing mismatch is not an error, it is an unfinished sentence: it is
  // only decided once the confirmation has been blurred, has reached the
  // password's length, or a submit has been attempted.
  paintMatch(password, confirmation) {
    const copy = this.copyValue.confirm
    let state = "idle"

    if (confirmation.length > 0) {
      const decided = this.confirmTouched || this.submitAttempted || confirmation.length >= password.length
      if (confirmation === password) state = "matched"
      else if (decided) state = "mismatched"
    }

    this.setLine(this.matchTarget, "password-policy__match", state, {
      icon: state === "matched" ? "bi-check-circle-fill" : (state === "mismatched" ? "bi-exclamation-circle" : ""),
      text: state === "matched" ? copy.matched : (state === "mismatched" ? copy.mismatched : "")
    })

    // A dedicated class, never Bootstrap's .is-invalid: that class stays the
    // server's alone (OperationFormHelper#field_css_class), so client and
    // server feedback can never contradict each other on the same element.
    this.confirmationTarget.classList.toggle("password-policy__input--mismatch", state === "mismatched")

    return state
  }

  paintGate(ready) {
    this.submitTarget.setAttribute("aria-disabled", String(!ready))
    this.submitTarget.classList.toggle("password-policy__submit--waiting", !ready)
  }

  // Strict priority: exactly one sentence, naming exactly one next action.
  // Caps Lock is NOT in this chain — it has its own line, because it is not a
  // reason the submit button is inert and this element is the button's whole
  // accessible description.
  paintStatus({ password, size, confirmation, tooLong, forbidden, match, ready }) {
    const copy = this.copyValue
    const policy = this.policyValue
    let tone = "info"
    let icon = ""
    let text = ""

    if (password.length === 0) {
      text = copy.reason.empty
    } else if (tooLong) {
      tone = "warn"; icon = "bi-exclamation-circle"
      text = copy.reason.too_long.replace("%{count}", String(policy.maxLength))
    } else if (forbidden) {
      tone = "warn"; icon = "bi-exclamation-circle"
      text = copy.reason.line_break
    } else {
      const unmet = this.states.find((state) => !state.met)
      if (unmet) {
        text = this.reasonFor(unmet.rule.code, policy.minLength - size)
      } else if (confirmation.length === 0) {
        text = copy.reason.confirm_empty
      } else if (match === "mismatched") {
        text = copy.reason.confirm_mismatch
      }
    }

    if (ready) text = ""

    this.setLine(this.statusTarget, "password-policy__status", tone, { icon, text })
    return text
  }

  // Rails plural forms are shipped as { one, other }; JS selects explicitly.
  reasonFor(code, remaining) {
    const reason = this.copyValue.reason.rules[code]
    if (typeof reason === "string") return reason
    const form = remaining === 1 ? reason.one : reason.other
    return form.replace("%{count}", String(remaining))
  }

  setLine(target, block, modifier, { icon, text }) {
    target.className = text ? `${block} ${block}--${modifier}` : block
    const iconEl = target.querySelector("[data-role='icon']")
    const textEl = target.querySelector("[data-role='text']")
    iconEl.className = icon ? `bi ${icon}` : ""
    textEl.textContent = text
  }

  // Exactly one narrator. Debounced so it never machine-guns per keystroke,
  // and only written when the composed sentence actually changes.
  scheduleAnnounce({ password, bucket, met, total, match, ready, advisory }) {
    clearTimeout(this.announceTimer)
    this.announceTimer = setTimeout(() => {
      const copy = this.copyValue.announce
      const parts = []

      if (ready) parts.push(copy.ready)
      else if (match === "mismatched") parts.push(copy.mismatched)
      else if (match === "matched") parts.push(copy.matched)
      else if (password.length > 0) {
        parts.push(copy.status
          .replace("%{strength}", this.copyValue.strength.levels[bucket])
          .replace("%{met}", String(met))
          .replace("%{total}", String(total)))
      }

      // The advisory is the single most useful thing this widget says — a
      // sighted user reads it, so it must be announced too. Appended, never
      // substituted.
      if (advisory) parts.push(advisory)

      // Caps Lock is a prefix, not a short-circuit: as a short-circuit it
      // announced once and then suppressed every subsequent update for the rest
      // of the session (the dedupe below sees the same sentence every time).
      if (this.capsLock) parts.unshift(this.copyValue.caps_lock)

      const sentence = parts.join(" ")
      if (sentence === this.lastAnnouncement) return
      this.lastAnnouncement = sentence
      this.announceTarget.textContent = sentence
    }, 500)
  }

  // role="alert" region, used only for a rejected submit. Cleared first so an
  // identical repeated rejection is still announced.
  announceBlocked() {
    const reason = this.lastReason || this.copyValue.reason.empty
    this.alertTarget.textContent = ""
    setTimeout(() => {
      this.alertTarget.textContent = this.copyValue.announce.blocked.replace("%{reason}", reason)
    }, 50)
  }
}
